import React from "react";
import "./EconTalkStyles.css"; // Example stylesheet import

export default function EconTalk() {
    return (
        <div className="econtalk-container">

            <p className="margin-right">
                It is a natural human urge to want my friends to love what I love and hate what I hate.
                But I have also failed enough times to know that that’s not a possibility.
                Nevertheless, I will give you my best description of the value I get from EconTalk. The show has
                been running since 2006, and I’ve been listening to it since 2015. <strong> Maybe you know some
                people whose school had a big effect on them and shaped the rest of their lives, and they
                felt connected and that they belonged. That is EconTalk for me. </strong> Although I’ve never had
                classmates and community around it, the results are simply undeniable. They are so integral
                that no view and framework in my mind could be completely separated from it. It is
                more of a source of curiosity than a source of information.
            </p>

            <p className="margin-left">
                The <strong>central recurring themes</strong> (I like) are the following. If you do not buy
                the premise of one of these ideas, it will be too hard for you to listen to EconTalk. But if
                you feel comfortable and curious about these, then please continue reading.
            </p>

            <p className="margin-right">
                <strong>Economics</strong>, which I would define broadly as inspecting visible incentives
                and how they shape systems. That may be running an art museum, operating an 18th-century
                pirate ship, dealing with other prisoners, kidnapping gangs in Somalia, or handling a
                healthcare system. Many episodes dive into the incentives each actor faces and how those
                incentives co-create a certain emergent outcome.
            </p>

            <p className="margin-right">
                <strong>Dignity, agency, the desire to be lovely, not only to be loved.</strong> Russ is a
                free-market defender regarding policies, but on a personal level, I get{" "}
                <em>spiritually socialist vibes</em> whenever he is passionate and emotional about dignity
                and agency. He finds them central to being human, and you find him pushing back on policies
                or views that rob some humans of their dignity. The socialist part that resonates with me is
                every life matters because of this universal struggle and this agency, which is rather in
                contrast with a vibe I get from many other podcasts that only the most talented, creative,
                productive individuals matter and are worthy of our attention and imitation.
            </p>

            <p className="margin-right">
                <strong>Tradition, religion, and history of ideas.</strong> Russ often questions the
                fundamental applications of the scientific method in topics like: human flourishing,
                morality, family, and the meaning of life. Instead, some other tools are discussed like
                tradition and religion. While science often wants to identify and solve problems
                indefinitely, given a blank slate, culture relies on what has survived the test of time and
                can only iterate and reflect. <em>[Listen to Nassim Taleb and John Grey if you want the tradition side]</em> –
                <em>[listen to Matt Ridley and Kevin Kelly for the science side]</em>
            </p>

            <p className="margin-right">
                <strong>Aspiration and Becoming.</strong> Challenges to make life-changing decisions,
                challenges to change our minds, our actions, or our desires, as seen through the lens of
                neuroscience, social science, and philosophy.
            </p>

            <p className="margin-left">
                I have made a Spotify playlist with <strong>15 episodes that I think are good to start with</strong>. Below
                you will find a short intro for each of the episodes on the playlist. No knowledge of
                economics is needed to follow any of them.
            </p>

            <p className="margin-left">
                If you already have some knowledge of Economics, Econometrist, Risk, Finance, or Statistics
                you can also browse by guest. I would go for any episode by <strong>James Heckman</strong>,{" "}
                <strong>Nassim Taleb</strong>, <strong>Michael Munger</strong>, <strong>Tyler Cowen</strong>
                , or <strong>Bryan Caplan</strong>.
            </p>

            <iframe
                style={{ borderRadius: "12px" }}
                src="https://open.spotify.com/embed/playlist/2Xq1ZwnjRC2nTdQgr8YMcu?utm_source=generator"
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
            />

            <h2 className="margin-right">Richard Davies on Extreme Economies</h2>
            <p className="margin-left">
                It might be my favorite, and one I have listened to the most. There is one story from it
                that I will probably never forget.
            </p>

            <p className="margin-left">
                When Syrian refugees poured into Jordan, two camps emerged:{" "}
                <strong><em>"Zaatari, a chaotic pop-up city, and Azraq, meticulously planned."</em></strong>{" "}
                Here are some moments from their story:
            </p>

            <p className="margin-left">
                <strong><em>"Zaatari grew at lightning speed, at one point reaching 200,000 people, like a city conjured from thin air."</em></strong>{" "}
                Refugees set up shops selling wedding dresses, running barbershops, and even opening a
                full-scale pool hall. Powdered milk became the gold standard: buy in bulk with e-credits,
                and sell outside for Jordanian dinars. Soon, currency circulated, letting entrepreneurs
                export tasty baked goods that even Jordanians came to buy.
            </p>

            <section className="highlight-section-podcast">
                <p>
                    Then <strong>Azraq</strong> was built “having learned the lesson of Zaatari” to eliminate
                    the chaotic, unregulated growth seen in it. Aid agencies aimed for fairness and safety and
                    applied tighter control to prevent smuggling and ensure a more uniform distribution of
                    necessities. Identical houses in neat rows, and a prebuilt market of just 100 stores.
                </p>
                <p>
                    Zaatari’s freewheeling vibe yielded 65% employment, while Azraq left only 9% of people
                    with jobs. Azraq, was a place refugees hoped to avoid. Azraq solved some logistical
                    problems, but it also restricted the very human need for autonomy and choice.
                </p>
            </section>

            <p className="margin-right">
                It is a lesson about emergent dynamics but at the same time a reminder to me that with all
                their good intentions, western help all too often falls into the trap of looking at poor
                countries as problems to be solved and populations that compared to their developed
                counterparts lack the agency and insight to decide what is good for them.
            </p>

            <div style={{ display: "flex", gap: "3%" , maxWidth: "100%" }}>
                <img
                    src="https://res.cloudinary.com/dl9xg597r/image/upload/v1740048390/download_cw3zat.webp"
                    alt="Zaatari"
                    className="image-style"
                style={{ width: "45%", objectFit: "cover" }}
                />
                <img
                    src="https://res.cloudinary.com/dl9xg597r/image/upload/v1740048390/17591146_1005_c46ydm.webp"
                    alt="Azraq"
                    className="image-style"
                style={{ width: "45%", objectFit: "cover" }}
                />
            </div>


            <h2 className="margin-right">Michael O'Hare on Art Museums</h2>
            <p className="margin-left">
                Michael talks about incentives in the Art Museums world where established peer pressure
                makes art directors fear backlash if they sell any art for reasons beyond buying more art.
                That has resulted in <strong>museums' mission to become to possess art not
                engage the public</strong> with it. Any major art museum displays less than 5% of
                the art it owns at any given time. It will never display more than about 8% of what it owns.
                He claims <strong>selling just 1% of a big museum’s collection could fund free admission forever</strong>,
                yet selling remains taboo.
            </p>

            <h2 className="margin-right">L.A. Paul on Vampires, Life Choices, and Transformation</h2>
            <p className="margin-left">
                How do you make a rational decision about something that will <strong>completely change who you are</strong>?
                Rational choice suggests you should gather information and pick what fits your current preferences.
                L.A. Paul finds that approach not complete when it comes to <em>transformative experiences</em>, decisions
                like becoming a vampire, having a child, or converting to a religion.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-right">
                    How could you possibly predict how you’d feel about immortality or nocturnal cravings if
                    becoming a vampire rewires your entire sense of self? Your current self can’t fully grasp
                    what your future self will value. Even other vampires can’t fully help, their advice is
                    colored by their own irreversible transformation.
                </p>
            </section>

            <h2 className="margin-right">David Epstein on Mastery, Specialization, and Range</h2>
            <p className="margin-right">
                Epstein challenges the philosophy of early, narrow specialization by arguing that the world
                we operate in isn’t uniformly predictable. In what he calls{" "}
                <strong><em>"kind environments"</em></strong>, settings where cause and effect are clear and feedback
                is immediate, repeating the same task builds expertise, such as in Golf or Chess.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-left">
                    But in <strong><em>"wicked environments"</em></strong>, where signals are noisy, feedback is delayed
                    or ambiguous, and rules are constantly shifting, the traditional route to mastery can
                    actually trap you. Epstein insists, If you’re confined to a single discipline in a wicked
                    environment, you risk being outmaneuvered by the unpredictable. Adaptability and a diverse
                    skillset become your greatest assets.
                </p>
            </section>

            <h2 className="margin-right">Agnes Callard on Aspiration</h2>
            <p className="margin-right">
                This episode shares a lot of the same concerns as the Vampire problem, which is how to{" "}
                <strong>become someone you are not.</strong> Agnes talks about the distinction between
                ambition and aspiration and makes the case for the latter. Aspiration in her work isn’t a
                static goal but a process, a continuous, evolving practice for self-transformation. This
                demands that you acknowledge the uncomfortable gap between who you are and who you might
                become. I enjoy listening to Agnes whenever she is on the show, I like how she frames things
                and looks at seemingly trivial reactions we have in our lives (e.g. aspiration or anger)
                from different lenses.
            </p>

            <h2 className="margin-right">Tyler Cowen on Food</h2>
            <p className="margin-right">
                Tyler has his own podcast, the second of the four I follow. Tyler is to my knowledge the
                biggest consumer of human goods, be it city life, music, food, books, ideas, and systems.
                Big here has three dimensions depth, breadth, and diversity.
            </p>

            <p className="margin-right">
                This is an episode, to find out how logistics, refrigeration technology, and regulation have
                influenced how food tastes in some countries.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-right">
                    There are tips on how to make yourself eat more vegetation without making yourself, to
                    look for a good restaurant in neighborhoods where schools are good but rents are not too
                    high, and which cookbooks not to buy.
                </p>
            </section>

            <p className="margin-right">
                On food safety, he says: <strong><em>"Well, cooked food is best; if you can see it, even better. I think street food is safer on average in most parts of the world.
                    If you know a little bit about municipal water supply you'll be able to figure out a lot of things.
                    I wouldn't eat a lot of street food in India, for instance. But in Mexico, I think it's the safest food."</em></strong>
            </p>

            <h2 className="margin-right">Michael Munger on Obedience to the Unenforceable</h2>
            <p className="margin-right">
                This is the conversation about the difference between <strong>"what is legal and what is right"</strong>,
                or <strong>"what I can take, and what I should take"</strong> which is something that for better
                or worse occupies my attention a lot. Sometimes it has led me to impose my moral values on
                others, even if the imposition never left my own head and just led to personal frustration.
            </p>

            <p className="margin-left">
                It explores a tension between personal freedom with the unwritten rules that hold society
                together. "Obedience to the unenforceable" captures the voluntary acts of integrity that
                keep societies functioning without constant oversight. These unspoken norms, like honesty,
                respect, or fairness, are fragile and yet vital since as they erode, societies often turn to
                laws to fill the gap. Over-reliance on dry enforceable rules lowers flexibility and trust,
                increases transaction costs to do anything, and weakens our ability to self-govern.
            </p>

            <h2 className="margin-right">Michael Munger on Slavery and Racism</h2>
            <p className="margin-right">
                What will future generations find equally repulsive as Slavery among the decisions we treat
                with casual discomfort now? I can think of some. I would put my bet on immigration policy. I
                think not unconditionally accepting persons who risk the life of their children to relocate,
                is morally wrong no matter the risk or cost analysis. Animal farms can be another good
                candidate. (I think it is likely, not signaling that I am vegetarian, in fact, I'm not).
                Climate change action and coordination would be my next guess.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-right">
                    Michael Munger argues that Many in the South were already uncomfortable with the idea but
                    they thought of it as a necessary evil.
                </p>
            </section>

            <p className="margin-right">
                They talk about the "Wolf by the ear" a phrase from Thomas Jefferson, describing a difficult
                situation where letting go is dangerous but holding on is also problematic. Economically,
                slavery was believed to be vital for labor-intensive crops like tobacco and cotton, and
                abolishing it without compensating slave owners risked economic collapse. Second, there
                could not be free blacks living among slave owners. At least some of them will be pretty
                angry that they've been treated, taken, wrested away from their homeland, kidnapped and
                forced to work for generations. We can't let them go and keep them here. Munger says{" "}
                <strong><em>"They had these justifications--these elaborately worked-out justifications--does make you wonder what 200 years from now, people will look back at our society and say, 'How could they have thought that?'"</em></strong>
            </p>

            <h2 className="margin-right">Matt Ridley on How Innovation Works</h2>
            <p className="margin-left">
                Innovation isn’t what we think it is. It’s not a lone genius having a eureka moment, it’s a
                slow, grinding process of trial and error, persistence, and collaboration. Matt Ridley
                argues that history has been rewritten to celebrate singular inventors like Edison and the
                Wright brothers when, in reality, they were just the ones who perfected ideas that many
                others were working on at the same time. Ridley insists, that innovation only thrives in
                environments where people are free to experiment, fail, and try again without needing
                permission.
            </p>

            <h2 className="margin-right">David McRaney on How Minds Change</h2>
            <p className="margin-left">
                This conversation partly made it to the list because I tried one of the tactics David talks
                about with my 10-year-old rebellious sister and I felt like I just got superpowers, it
                worked like acid on metal. So I had to add it.
            </p>

            <p className="margin-left">
                We like to believe we change our minds through reason, facts, and careful analysis. But when
                we change, it’s rarely because someone proved us wrong. More often, it’s because something
                shifted our motivations, our sense of belonging, our emotional ties to an idea.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-left">
                    David talks about the psychology of belief, persuasion, and why we cling to ideas, even
                    when the evidence is stacked against us. He explains how our brains aren’t wired for
                    truth-seeking but for justification, how social influence trumps logic, and why simply
                    giving people "the facts" almost never works.
                </p>
            </section>

            <h2 className="margin-right">Benedict Evans on the Future of Cars</h2>
            <p className="margin-left">
                Recorded in 2017 this conversation sounds still ahead of the current time (2025). We often
                struggle to imagine how technology will truly change our lives, focusing only on the obvious
                upgrades, like a faster car or a quieter engine. But the change lies in the unexpected ripple
                effects.
            </p>

            <p className="margin-left">
                The guest explores how electric and autonomous cars will reshape industries, cities, and
                daily life. Electric cars simplify mechanics, disrupting supply chains, repair shops, and
                gas stations. Autonomous vehicles could eliminate accidents, reduce car ownership, and even
                change urban design by freeing up space currently used for parking.
            </p>

            <p className="margin-left">
                Imagine a town with an electric, self-driving bus. At first, it’s just convenient. But over
                time: businesses thrive without parking hassles, elderly residents regain independence, and
                parking lots become parks. Technological change's power is not just in solving problems but
                in creating new possibilities.
            </p>

            <h2 className="margin-right">Nassim Nicholas Taleb on Skin in the Game</h2>
            <p className="margin-right">
                <strong>Skin in the game</strong> is what keeps people honest. If you make a decision, you
                should feel the consequences, good or bad. In the past, a reckless general died with his
                army, and a bad merchant went broke. This natural balance ensured that power and
                responsibility stayed connected. When people had direct exposure to their own risks, they
                made smarter choices, or they didn’t last. It was survival of the most accountable.
            </p>

            <p className="margin-right">
                Modern society has weakened this link. <strong>Politicians pass laws but don’t suffer when they
                fail. Bankers gamble with other people’s money and get bailed out when things go south. </strong>
                Bureaucrats regulate industries they don’t understand, while academics push theories with no
                real-world testing. The people making the decisions aren’t the ones paying the price, so
                they make reckless, self-serving choices.
            </p>

            <p className="margin-right">
                Any episode with <strong>Nassim Taleb</strong> is delightful, I hope Russ will have him on
                again. But Taleb also randomly changed the course of my life. My mom was and is a fast and
                enthusiastic reader, she could not fall asleep without a book. So naturally I had tried many
                of her books and by age 18 I had decided “I do not like books”, she only read fiction. When I
                was 24, a friend of mine gave me <em>Black Swan by Taleb</em>, I only read it since I had to
                wait hours for the simulations I was running at the time. It changed my life to realize “I do
                not like books” was wrong, I was really into non-fiction.
            </p>

            <p className="margin-right">
                That is the problem with definitions and suitcase words, which I think “Book” or “Reading”
                is one. We default to overgeneralizing sometimes for no better reason than a lack of proper
                words.
            </p>

            <p className="margin-left" style={{textAlign: "center"}}>
                ****
            </p>

            <p className="margin-left" style={{marginTop: "5rem", marginBottom: "3rem"}}>
            <strong>So far you had episodes that feel good to listen to, maybe they even confirmed some of your
                gut feelings or validated you. The last three will be more demanding to listen to, so
                proceed at your own risk. They might challenge some central views you hold and create
                tension, not exactly the best to start with, but there are always some people who get a kick
                out of provocation.</strong>
            </p>

            <h2 className="margin-right">Chris Arnade on Dignity</h2>
            <p className="margin-right">
                As an immigrant kid who never really stopped feeling guilty about leaving and giving up,
                this one hits close to home. Chris, a former Wall Street trader, spent years making
                decisions based on numbers, but then he began exploring forgotten neighborhoods. He met drug
                addicts, prostitutes, and the homeless, who were often reduced to statistics but had{" "}
                <strong>dignity and community that the numbers failed to talk about.</strong>
            </p>

            <p className="margin-right">
                His point is home is identity, belonging, and stability. While elite society values mobility
                and credentials, many people in small towns choose to stay, not out of ignorance or failure,
                but because they value their communities in ways outsiders don’t understand. Even in places
                filled with economic despair, there is pride in knowing the land, and in maintaining
                relationships that have lasted generations. It sets a challenge for a society that defines
                success overly through money and mobility while ignoring the importance of faith, family,
                and place.
            </p>

            <h2 className="margin-right">Robert Sapolsky on Determinism, Free Will, and Responsibility</h2>
            <section className="highlight-section-podcast">
                <p className="margin-right">
                    Ask yourself what's the difference between believing in free will and believing in God.
                    Historically, both beliefs were equally widespread. They share several similarities:
                </p>
                <p className="margin-left">
                    Science has challenged both but neither confirmed nor disproven either. People can sense
                    the presence of both. These are among the oldest beliefs sustained throughout human
                    history. Societies have felt that without these beliefs, no ultimate justice (God) or no
                    responsibility (free will), chaos could ensue, as people act without regard for
                    consequences.
                </p>
            </section>

            <p className="margin-right">
            <strong>What if free will is just a story your brain tells you after the fact? What if every
                decision, what you had for breakfast, and who you married, was already set in motion long
                before you even thought about it? </strong>Robert (Neuroscientist) has come to believe that’s exactly
                how it works. No free will, no true choices, just biology and circumstance playing out.
            </p>

            <p className="margin-right">
                He starts dropping evidence. Put someone in a room with a disgusting smell, and they’ll
                answer political questions more conservatively. Judges rule more harshly when they’re
                hungry. Brain scans show your neurons fire to make a decision before you even realize you’ve
                made it. Even your personality, whether you're impulsive, disciplined, kind, or vengeful, was
                shaped by everything from fetal hormone levels to whether your great-great-grandparents
                were farmers or herders. They then ask why we feel regret. Why do we praise and blame? And
                if we accept this view, what happens to justice? To meaning? To the very idea that we should
                try to be better?
            </p>

            <h2 className="margin-right">John Gray on the Seven Kinds of Atheism</h2>
            <p className="margin-left">
                John Gray begins with the ancient world, a time when the idea of indefinite human improvement
                would have been incomprehensible. <strong>In the polytheistic traditions of Asia, Greece, and Rome,
                history had no direction, no grand arc bending toward justice, no final destination where
                humanity would reach its full potential. </strong>The gods themselves were in eternal conflict, each
                embodying different and often contradictory values with no expectation that these values
                could ever be reconciled.
            </p>

            <p className="margin-left">
                This polytheistic worldview led to a kind of tolerance, at least in principle. Beauty and
                pleasure were as much a part of the good life as discipline and morality. The Greeks and
                Romans did not imagine that history had a single meaning. There were Greek histories, Roman
                histories, and Jewish histories, each a self-contained narrative with its values, none
                needing to be merged into a singular vision of the human story.
            </p>

            <p className="margin-left">
                Then came Christianity, and with it, a radical shift in thinking. The Christian world was no
                longer cyclical but linear; history was now a story of redemption, a movement toward
                salvation orchestrated by divine providence.<strong> Humanity was no longer a collection of
                disparate peoples following their own paths, but a single collective with a common destiny.</strong>
                In Christianity’s wake, the idea of a universal human history emerged. Tolerance, once a
                necessity in a world of many gods, gave way to the urgency of salvation.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-right">
                    Modern atheism, Gray argues, has not freed itself from this Christian inheritance. While
                    rejecting God, it has preserved the Christian belief in a common human agency and a shared
                    moral trajectory.
                </p>
            </section>

            <p className="margin-left">
                Today’s secular humanists believe that history is not just a series of events but a story of
                progress. They assume that human beings are improving, not just technologically, but morally.
                But improvement implies a goal, an ultimate destination toward which humanity is moving.
                This is not a belief grounded in science but in faith, inherited from the very monotheistic
                traditions that secularists claim to have left behind.
            </p>

            <p className="margin-left">
                The idea of progress is intimately tied to the belief in universal human values.
                <strong> Christianity once provided these values through divine commandments; modern humanists now seek to ground them in reason and science. </strong>
                But here, Gray argues, is where the illusion becomes most dangerous. Many modern thinkers
                claim that science can determine morality and that ethical questions can be resolved with
                the same clarity as mathematical equations.
            </p>

            <section className="highlight-section-podcast">
                <p className="margin-left">
                    If values can be derived scientifically, then there is no longer any need for
                    tolerance, those who resist the truth are simply wrong.
                </p>
            </section>
        </div>
    );
}
